import React, { useState } from 'react';
import { getNetWorth, projectFinances, sourcesToMonthly } from '../utils';
import { SvgLineChart } from './SvgLineChart';

const ASSET_TYPES = [
  { id: 'savings',     label: 'Savings Account', em: '🏦' },
  { id: 'investment',  label: 'Investments',      em: '📈' },
  { id: 'property',   label: 'Property',          em: '🏠' },
  { id: 'vehicle',    label: 'Vehicle',            em: '🚗' },
  { id: 'retirement', label: 'Retirement (401k)', em: '👴' },
  { id: 'crypto',     label: 'Crypto',            em: '₿' },
  { id: 'other',      label: 'Other Asset',       em: '💼' },
];

const f0 = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function NetWorthTracker({ state, updateState, showToast }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'savings', value: '' });
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');

  const assets = state.assets || [];
  const { assets: totalAssets, debts, netWorth } = getNetWorth(state);
  const savingsTotal = Object.values(state.savDeposits || {}).flat().reduce((s, d) => s + (d.amt || 0), 0);
  const monthlyIncome = state.monthlyIncome || sourcesToMonthly(state.incomeSources || []);
  const projection = projectFinances(state, 60);
  const trajectory = projection
    ? [12, 36, 60].map((m) => ({
        label: m === 12 ? '1 yr' : m === 36 ? '3 yr' : '5 yr',
        value: Math.round(projection.points[m - 1].netWorth),
      }))
    : [];
  const fmtMo = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const addAsset = () => {
    if (!form.name.trim() || !form.value) return;
    const asset = { id: 'ast_' + Date.now(), name: form.name.trim(), type: form.type, value: parseFloat(form.value) || 0 };
    updateState({ assets: [...assets, asset] });
    setForm({ name: '', type: 'savings', value: '' });
    setAdding(false);
    showToast('Asset added!');
  };

  const removeAsset = (id) => {
    updateState({ assets: assets.filter(a => a.id !== id) });
  };

  const saveEdit = (id) => {
    updateState({ assets: assets.map(a => a.id === id ? { ...a, value: parseFloat(editVal) || 0 } : a) });
    setEditId(null);
  };

  const nwColor = netWorth >= 0 ? 'var(--grn)' : 'var(--red)';
  const barPct = totalAssets > 0 ? Math.min(100, Math.round((totalAssets / (totalAssets + debts)) * 100)) : 0;

  return (
    <div>
      {/* Net Worth hero */}
      <div className="card cp" style={{ marginBottom: 14, textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>NET WORTH</div>
        <div style={{ fontSize: 38, fontWeight: 900, color: nwColor, marginBottom: 8 }}>{f0(netWorth)}</div>

        {/* Assets vs Debts bar */}
        <div style={{ height: 10, borderRadius: 999, background: 'var(--surf2)', overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ height: '100%', width: barPct + '%', background: 'var(--grn)', borderRadius: 999, transition: 'width .5s' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--mut)', marginBottom: 14 }}>
          <span style={{ color: 'var(--grn)', fontWeight: 700 }}>Assets {f0(totalAssets)}</span>
          <span style={{ color: 'var(--red)', fontWeight: 700 }}>Debts {f0(debts)}</span>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { l: 'Assets', v: f0(totalAssets), c: 'var(--grn)' },
            { l: 'Debts',  v: f0(debts),       c: 'var(--red)' },
            { l: 'Net',    v: f0(netWorth),     c: nwColor },
          ].map(s => (
            <div key={s.l} style={{ flex: 1, minWidth: 90, padding: '8px 10px', background: 'var(--surf2)', borderRadius: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Trajectory */}
      {monthlyIncome > 0 && projection && (
        <div className="card cp" style={{ marginBottom: 14 }}>
          <div className="st" style={{ marginBottom: 12 }}>🔮 Net Worth Trajectory</div>
          <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 10 }}>
            If you follow your Autopilot plan every month: bills, spending, emergency fund, then debt (with interest) and goals.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {trajectory.map(t => {
              const col = t.value >= 0 ? 'var(--grn)' : 'var(--red)';
              return (
                <div key={t.label} style={{ flex: 1, padding: '10px 8px', background: 'var(--surf2)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: col }}>{f0(t.value)}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>{t.label} from now</div>
                  <div style={{ fontSize: 9, color: t.value >= netWorth ? 'var(--grn)' : 'var(--red)', marginTop: 2, fontWeight: 700 }}>
                    {t.value >= netWorth ? '▲' : '▼'} {f0(Math.abs(t.value - netWorth))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 14 }}>
            <SvgLineChart
              data={projection.points}
              series={[
                { k: 'saved', c: 'var(--grn)', w: 2.5, fill: true },
                { k: 'debt', c: 'var(--red)', w: 2.5 },
              ]}
              height={170}
            />
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>
              <span><span style={{ color: 'var(--grn)', fontWeight: 800 }}>━</span> Saved from here on</span>
              <span><span style={{ color: 'var(--red)', fontWeight: 800 }}>━</span> Debt left</span>
            </div>
          </div>
          {projection.milestones.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {projection.milestones.slice(0, 6).map((m) => (
                <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderTop: '1px solid var(--bdr)' }}>
                  <span style={{ fontWeight: 600 }}>🏁 {m.label}</span>
                  <span style={{ fontWeight: 700, color: 'var(--grn)' }}>{fmtMo(m.date)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 9, color: 'var(--mut)', marginTop: 8 }}>
            Assumes today's income, bills and budgets stay the same. Asset values aren't grown.
          </div>
        </div>
      )}

      {/* Assets list */}
      <div className="card cp" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="st" style={{ marginBottom: 0 }}>📈 Assets</div>
          <button onClick={() => setAdding(s => !s)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.1)', color: 'var(--ylw)', cursor: 'pointer', fontWeight: 700 }}>
            + Add Asset
          </button>
        </div>

        {adding && (
          <div style={{ padding: '12px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              {ASSET_TYPES.map(t => (
                <button key={t.id} onClick={() => setForm(f => ({ ...f, type: t.id }))} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${form.type === t.id ? 'var(--ylw)' : 'var(--bdr)'}`, background: form.type === t.id ? 'rgba(255,217,61,.12)' : 'none', color: form.type === t.id ? 'var(--ylw)' : 'var(--mut)' }}>
                  {t.em} {t.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="ob-inp" placeholder="Name (e.g. Vanguard 401k)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 2, margin: 0 }} />
              <input className="ob-inp" type="number" placeholder="Value $" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} style={{ flex: 1, margin: 0 }} />
              <button onClick={addAsset} style={{ padding: '8px 14px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>Add</button>
            </div>
          </div>
        )}

        {/* Savings from savings log (read-only) */}
        {savingsTotal > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bdr)' }}>
            <span style={{ fontSize: 22 }}>🐷</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Savings Goal Fund</div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>From your savings log</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--grn)' }}>{f0(savingsTotal)}</div>
          </div>
        )}

        {assets.length === 0 && savingsTotal === 0 ? (
          <div className="em">No assets yet — add your first one above!</div>
        ) : (
          assets.map(a => {
            const t = ASSET_TYPES.find(x => x.id === a.type) || ASSET_TYPES[6];
            return (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bdr)' }}>
                <span style={{ fontSize: 22 }}>{t.em}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>{t.label}</div>
                </div>
                {editId === a.id ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input className="ob-inp" type="number" value={editVal} onChange={e => setEditVal(e.target.value)} style={{ width: 90, margin: 0, fontSize: 12 }} />
                    <button onClick={() => saveEdit(a.id)} style={{ padding: '4px 10px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>✓</button>
                    <button onClick={() => setEditId(null)} style={{ padding: '4px 8px', borderRadius: 8, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', cursor: 'pointer', fontSize: 11 }}>✕</button>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--grn)', cursor: 'pointer' }} onClick={() => { setEditId(a.id); setEditVal(a.value); }}>
                      {f0(a.value)}
                    </div>
                    <button onClick={() => removeAsset(a.id)} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>✕</button>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Debts breakdown */}
      <div className="card cp">
        <div className="st" style={{ marginBottom: 12 }}>💳 Debts</div>
        {(state.cards || []).length === 0 ? (
          <div className="em">No debts — amazing! 🎉</div>
        ) : (
          (state.cards || []).map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
              <span style={{ fontSize: 20 }}>💳</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>{c.apr}% APR</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--red)' }}>{f0(c.bal)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
